import Decimal from "decimal.js-light";

import {
  PRODUCT_TYPE_LABOUR_PRODUCT_LABEL,
  PRODUCT_TYPE_PRODUCT_KIT_LABEL,
} from "@/utils/constants";

import { getLabourLineItems } from "./get-labour-line-items";

Decimal.config({ rounding: Decimal.ROUND_HALF_UP });

/**
 * Applies an item-level discount to a product total.
 * Supports both percentage and dollar discounts based on the discountIsPercentage flag.
 * - Percentage: productTotal × (1 - discount/100), rounded to 2dp.
 * - Dollar: productTotal - discount, rounded to 2dp.
 * @param {Object} params
 * @param {Decimal} params.productTotal - The pre-discount total (value × quantity).
 * @param {Decimal} params.discount - The discount amount (percentage value or dollar amount).
 * @param {boolean} params.discountIsPercentage - false for dollar discount; true/undefined for percentage.
 * @returns {Decimal} The discounted total, rounded to 2 decimal places.
 */
const applyItemDiscount = ({ productTotal, discount, discountIsPercentage }) => {
  if (discountIsPercentage === false) {
    return productTotal.minus(discount).toDecimalPlaces(2);
  }
  const discountRate = discount.dividedBy(100).toDecimalPlaces(2);
  return productTotal.times(Decimal(1).minus(discountRate)).toDecimalPlaces(2);
};

/**
 * Calculates the price for a single labour item after applying its item-level discount.
 * Price = value × quantity, then discount applied. Returns Decimal(0) if item has no value.
 * @param {Object} item - A labour line item with value, quantity, discount, and discountIsPercentage.
 * @returns {Decimal} The discounted price for this labour item, rounded to 2dp.
 */
const calcLabourItemPrice = (item) => {
  if (!item.value) {
    return Decimal(0);
  }
  const itemDiscount = Decimal(item.discount ? item.discount : 0);
  const productTotal = Decimal(item.value).times(item.quantity);

  return applyItemDiscount({
    productTotal,
    discount: itemDiscount,
    discountIsPercentage: item.discountIsPercentage,
  });
};

/**
 * Calculates the total price of all labour items across order line items, product kits, and sub-kits.
 * Filters to labour-relevant items, then sums each labour item's discounted price
 * (value × quantity minus item-level discount). Skips deleted items.
 * @param {Object} params
 * @param {Array} params.items - All order line items.
 * @returns {Decimal} The unrounded total labour price.
 */
const calculateTotalLabourPrice = ({ items }) => {
  const totalPrice = getLabourLineItems(items).reduce((sum, item) => {
    if (item.isDeleted) {
      return Decimal(sum);
    }

    if (item.productType === PRODUCT_TYPE_PRODUCT_KIT_LABEL) {
      if (!Array.isArray(item.productKitItems)) {
        return Decimal(sum);
      }

      const kitMarkup = item.productKitItems
        .filter((kitItem) => kitItem.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL)
        .reduce((kitSum, kitItem) => Decimal(kitSum).plus(calcLabourItemPrice(kitItem)), 0);

      const subKitsMarkup = Array.isArray(item.subKits)
        ? item.subKits
            .flatMap((i) =>
              i.kitProducts
                .filter((k) => k.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL)
                .map((k) => ({ ...k }))
            )
            .reduce((kitSum, kitItem) => Decimal(kitSum).plus(calcLabourItemPrice(kitItem)), 0)
        : Decimal(0);

      return Decimal(sum).plus(kitMarkup).plus(subKitsMarkup);
    }

    if (item.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
      return Decimal(sum).plus(calcLabourItemPrice(item));
    }

    /* istanbul ignore next */
    return Decimal(sum);
  }, 0);

  return totalPrice;
};

export default calculateTotalLabourPrice;
